import { useMemo, useCallback } from 'react';
import { trpc } from '@org/clients/school';
import { unwrapTrpcData } from '../../../utils';
import { ContentType } from '../../../types/contents';
import { ExerciseType } from '@org/model';
import type { ExerciseDefinition, ExerciseSubmissionPayload } from '@org/exercises';
import { 
  buildExerciseDefinition as buildExerciseDefinitionFromServer,
  buildMatchingPairs as buildMatchingPairsFromDetails,
  inferExerciseSubtype,
} from '@org/exercises';

export interface StudentBlock {
  id: string;
  name: string;
  description?: string | null;
  order: number;
  contents: StudentContent[];
}

interface BaseStudentContent {
  id: string;
  title?: string | null;
  description?: string | null;
  order: number;
  type: ContentType;
}

export interface StudentTextContent extends BaseStudentContent {
  type: ContentType.Text;
  text: string;
  format: 'plain' | 'markdown' | 'html';
}

export interface StudentImageContent extends BaseStudentContent {
  type: ContentType.Image;
  url: string;
  ext?: string | null;
}

export interface StudentVideoContent extends BaseStudentContent {
  type: ContentType.Video;
  url: string;
  ext?: string | null;
}

export interface StudentAudioContent extends BaseStudentContent {
  type: ContentType.Audio;
  url: string;
  length?: number | null;
}

export interface StudentDocumentContent extends BaseStudentContent {
  type: ContentType.Document;
  url: string;
  isPdf?: boolean;
  isWord?: boolean;
}

export interface StudentExerciseContent extends BaseStudentContent {
  type: ContentType.Exercise;
  exerciseId: string;
  exerciseSubtype: ExerciseType;
  correction?: string | null;
  question?: string | null;
  options?: Array<{
    optionId?: string;
    label: string;
    isCorrect: boolean;
    order?: number;
  }>;
  format?: 'plain' | 'markdown' | 'html';
  textType?: 'short' | 'long';
  maxDurationSec?: number | null;
  sentences?: Array<{
    sentenceId?: string;
    sentence?: string;
    correctSentence?: string; // For Scramble exercises
    answer?: string;
    correction?: string | null;
    order?: number;
    blanks?: Array<{
      blankId?: string;
      position?: number;
      prompt?: string;
      options: Array<{
        optionId?: string;
        label: string;
        isCorrect?: boolean;
        order?: number;
      }>;
    }>;
  }>;
  // Conversational exercise specific fields
  theme?: string;
  systemPrompt?: string | null;
  targetSkills?: string[] | null;
  // Matching exercise specific fields
  pairs?: Array<{
    pairId?: string;
    promptId?: string;
    promptLabel: string;
    optionId?: string;
    optionLabel: string;
    order?: number;
  }>;
  evaluationDefinition?: ExerciseDefinition | null;
  lastSubmission?: ExerciseSubmissionPayload | null;
  lastEvaluation?: Record<string, unknown> | null;
}

export type StudentContent =
  | StudentTextContent
  | StudentImageContent
  | StudentVideoContent
  | StudentAudioContent
  | StudentDocumentContent
  | StudentExerciseContent;

interface UseClassBlocksOptions {
  classId?: string;
  blockIdx?: number;
  enabled?: boolean;
}

export function useClassBlocks({
  classId,
  blockIdx,
  enabled = true,
}: UseClassBlocksOptions) {
  const shouldFetch = Boolean(classId && classId.trim() !== '' && enabled);

  const normalizedBlockIdx = useMemo(() => {
    if (blockIdx === undefined || Number.isNaN(blockIdx)) {
      return undefined;
    }
    return blockIdx < 0 ? undefined : blockIdx;
  }, [blockIdx]);

  const blocksQuery = trpc.blocks.getBlocksByClass.useQuery(
    { classId: classId ?? '', blockIdx: normalizedBlockIdx },
    {
      enabled: shouldFetch,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  const normalizedBlocks = useMemo(
    () => unwrapTrpcData(blocksQuery.data) ?? [],
    [blocksQuery.data]
  );

  const blockContentQueries = trpc.useQueries((t) =>
    shouldFetch && normalizedBlocks.length
      ? normalizedBlocks.map((block) =>
          t.blocks.getBlockContents(
            { blockId: block.blockId },
            {
              enabled: Boolean(block.blockId && block.blockId.trim() !== ''),
              staleTime: Infinity,
              refetchOnMount: false,
              refetchOnWindowFocus: false,
              refetchOnReconnect: false,
            }
          )
        )
      : []
  );

  const contentQueries = trpc.useQueries((t) => {
    if (!shouldFetch) {
      return [];
    }

    if (
      !blockContentQueries.length ||
      blockContentQueries.some((query) => query.isLoading || !query.data)
    ) {
      return [];
    }

    const allContents = blockContentQueries
      .map((query) => unwrapTrpcData(query.data) ?? [])
      .flat();

    return allContents
      .map((contentItem) => {
        const unwrapped = unwrapTrpcData(contentItem) ?? contentItem;
        const content = unwrapped?.content;
        if (!content) {
          return null;
        }

        const contentId = content.contentId;
        const contentType = content.contentType;
        const isEnabled = Boolean(contentId && contentId.trim() !== '');

        switch (contentType) {
          case 'text':
            return t.content.getTextContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          case 'image':
            return t.content.getImageContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          case 'video':
            return t.content.getVideoContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          case 'audio':
            return t.content.getAudioContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          case 'document':
            return t.content.getDocumentContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          case 'exercise':
            return t.content.getExerciseContent(
              { contentId },
              {
                enabled: isEnabled,
                staleTime: Infinity,
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
              },
            );
          default:
            return null;
        }
      })
      .filter(Boolean) as ReturnType<typeof t.content.getTextContent>[];
  });

  const isLoadingBlocks = blocksQuery.isLoading;
  const isLoadingBlockContents = blockContentQueries.some(
    (query) => query.isLoading || query.isFetching
  );
  const isLoadingDefinitions = contentQueries.some(
    (query) => query.isLoading || query.isFetching
  );

  const contentDefinitionMap = useMemo(() => {
    const map = new Map<string, any>();

    if (!blockContentQueries.length) {
      return map;
    }

    const allContents = blockContentQueries
      .map((query) => unwrapTrpcData(query.data) ?? [])
      .flat();

    allContents.forEach((contentItem, index) => {
      const unwrapped = unwrapTrpcData(contentItem) ?? contentItem;
      const contentId = unwrapped?.content?.contentId;
      if (!contentId) {
        return;
      }

      const definitionQuery = contentQueries[index];
      const definitionArray = definitionQuery?.data as any[] | undefined;
      if (!definitionArray || !definitionArray.length) {
        return;
      }

      map.set(contentId, definitionArray[0]);
    });

    return map;
  }, [blockContentQueries, contentQueries]);

  const blocks: StudentBlock[] = useMemo(() => {
    if (!normalizedBlocks.length || !blockContentQueries.length) {
      return [];
    }

    const orderedBlocks = [...normalizedBlocks].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );

    return orderedBlocks.map((block) => {
      const blockIndex = normalizedBlocks.findIndex(
        (originalBlock) => originalBlock.blockId === block.blockId
      );
      const contentsEnvelope =
        blockIndex >= 0 ? blockContentQueries[blockIndex]?.data : undefined;
      const contents = unwrapTrpcData(contentsEnvelope) ?? [];

      const normalizedContents = contents
        .map((contentItem) => {
          const unwrapped: any = unwrapTrpcData(contentItem) ?? contentItem;
          const content = unwrapped?.content;
          if (!content) {
            return null;
          }

          const definition = contentDefinitionMap.get(content.contentId);
          const order = (unwrapped?.order ?? 0) as number;

          return mapToStudentContent({
            content,
            definition,
            order,
          });
        })
        .filter(Boolean) as StudentContent[];

      return {
        id: block.blockId,
        name: block.name,
        description: block.description,
        order: block.order ?? 0,
        contents: normalizedContents.sort((a, b) => a.order - b.order),
      };
    });
  }, [normalizedBlocks, blockContentQueries, contentDefinitionMap]);

  const refetch = useCallback(() => {
    void blocksQuery.refetch();
    blockContentQueries.forEach((query) => {
      if (typeof query.refetch === 'function') {
        void query.refetch();
      }
    });
    contentQueries.forEach((query) => {
      if (query && typeof query.refetch === 'function') {
        void query.refetch();
      }
    });
  }, [blocksQuery, blockContentQueries, contentQueries]);

  return {
    blocks,
    isFiltered: normalizedBlockIdx !== undefined,
    isLoading: isLoadingBlocks || isLoadingBlockContents || isLoadingDefinitions,
    refetch,
  };
}

function mapToStudentContent({
  content,
  definition,
  order,
}: {
  content: any;
  definition: any;
  order: number;
}): StudentContent | null {
  const base: BaseStudentContent = {
    id: content.contentId,
    title: content.title,
    description: content.description,
    order,
    type: toContentType(content.contentType),
  };

  switch (base.type) {
    case ContentType.Text:
      return {
        ...base,
        text: definition?.text ?? '',
        format: definition?.format ?? 'plain',
      } as StudentContent;
    case ContentType.Image:
      return {
        ...base,
        url: definition?.url ?? '',
        ext: definition?.ext,
      } as StudentContent;
    case ContentType.Video:
      return {
        ...base,
        url: definition?.url ?? '',
        ext: definition?.ext,
      } as StudentContent;
    case ContentType.Audio:
      return {
        ...base,
        url: definition?.url ?? '',
        length: definition?.length,
      } as StudentContent;
    case ContentType.Document:
      return {
        ...base,
        url: definition?.url ?? '',
        isPdf: definition?.isPdf ?? false,
        isWord: definition?.isWord ?? false,
      } as StudentContent;
    case ContentType.Exercise: {
      const exerciseDetails = definition?.exerciseDetails ?? {};
      const exerciseId: string | undefined = definition?.exerciseId;
      if (!exerciseId) {
        return null;
      }
      const subtype = inferExerciseType(exerciseDetails?.type ?? definition?.exerciseType);
      const evaluationDefinition = buildExerciseDefinition(definition);
      const isPromptOnly =
        subtype === ExerciseType.LongAnswer ||
        subtype === ExerciseType.ShortAnswer ||
        subtype === ExerciseType.Recorded ||
        subtype === ExerciseType.Conversational;

      return {
        ...base,
        exerciseId,
        exerciseSubtype: subtype,
        correction: isPromptOnly ? null : definition?.correction,
        question: exerciseDetails?.question,
        options: exerciseDetails?.options ?? [],
        format: exerciseDetails?.format,
        textType: exerciseDetails?.textType,
        maxDurationSec: exerciseDetails?.maxDurationSec,
        sentences: exerciseDetails?.sentences ?? [],
        // Conversational exercise fields
        theme: exerciseDetails?.theme,
        systemPrompt: exerciseDetails?.systemPrompt,
        targetSkills: exerciseDetails?.targetSkills,
        // Matching exercise fields
        pairs: buildMatchingPairs(exerciseDetails),
        evaluationDefinition,
      } as StudentContent;
    }
    default:
      return null;
  }
}

/**
 * Build an ExerciseDefinition from server response data.
 * Uses the shared utility from @org/exercises.
 */
function buildExerciseDefinition(definition: any): ExerciseDefinition | null {
  if (!definition?.exerciseId) {
    return null;
  }

  return buildExerciseDefinitionFromServer({
    exerciseId: definition.exerciseId,
    exerciseType: definition.exerciseType,
    exerciseDetails: definition.exerciseDetails,
    title: definition.title,
    description: definition.description,
    correction: definition.correction,
    maxScore: definition.maxScore,
    passingScore: definition.passingScore,
  });
}

function toContentType(contentType: string): ContentType {
  switch (contentType.toLowerCase()) {
    case 'text':
      return ContentType.Text;
    case 'image':
      return ContentType.Image;
    case 'video':
      return ContentType.Video;
    case 'audio':
      return ContentType.Audio;
    case 'document':
      return ContentType.Document;
    case 'exercise':
      return ContentType.Exercise;
    default:
      return ContentType.Text;
  }
}

/**
 * Infer the ExerciseType enum value from a type string.
 * Uses the shared utility from @org/exercises.
 */
function inferExerciseType(type?: string): ExerciseType {
  const subtype = inferExerciseSubtype(type);
  return ExerciseType[subtype as keyof typeof ExerciseType] ?? ExerciseType.MultipleChoice;
}

/**
 * Build pairs for matching exercises.
 * Uses the shared utility from @org/exercises.
 */
function buildMatchingPairs(exerciseDetails: any): Array<{
  pairId?: string;
  promptId?: string;
  promptLabel: string;
  optionId?: string;
  optionLabel: string;
  order?: number;
}> | undefined {
  return buildMatchingPairsFromDetails(exerciseDetails ?? {});
}
